import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Grid, Paper, List, ListItem, ListItemText, Divider, CircularProgress, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ChevronLeft, ChevronRight, Add, Delete } from '@mui/icons-material';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';

const AgendaComponent = ({ onEventsChange }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            try {
                const month = currentMonth.getMonth() + 1;
                const year = currentMonth.getFullYear();
                const res = await api.get(`/agenda?mes=${month}&ano=${year}`);
                if (res.data.status === 'sucesso') {
                    // Normalize dates
                    const formattedEvents = res.data.dados.map(e => {
                        const parts = e.dt_evento.split('-');
                        const eventDate = new Date(parts[0], parts[1] - 1, parts[2]);
                        return {
                            ...e,
                            date: eventDate,
                            title: e.st_titulo,
                            time: e.st_hora
                        };
                    });
                    setEvents(formattedEvents);
                }
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Não foi possível carregar a agenda pessoal:', error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, [currentMonth]);

    const renderHeader = () => {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                    <ChevronLeft />
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">
                    {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
                </Typography>
                <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                    <ChevronRight />
                </IconButton>
            </Box>
        );
    };

    const renderDays = () => {
        const dateFormat = "EEEEE";
        const days = [];
        let startDate = startOfWeek(currentMonth, { weekStartsOn: 0 }); // Sunday

        for (let i = 0; i < 7; i++) {
            days.push(
                <Grid item xs={1.7} key={i} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {format(addDays(startDate, i), dateFormat, { locale: ptBR }).toUpperCase()}
                    </Typography>
                </Grid>
            );
        }

        return <Grid container spacing={0} sx={{ mb: 1 }}>{days}</Grid>;
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = "";

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, "d");
                const cloneDay = day;
                const isSelected = isSameDay(day, selectedDate);
                const isCurrentMonth = isSameMonth(day, monthStart);
                // Check if any event falls on this day
                const dayEvents = events.filter(e => isSameDay(e.date, day));

                days.push(
                    <Grid item xs={1.7} key={day} sx={{ textAlign: 'center', mb: 1 }}>
                        <Box
                            onClick={() => setSelectedDate(cloneDay)}
                            sx={{
                                height: 32,
                                width: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                margin: '0 auto',
                                cursor: 'pointer',
                                bgcolor: isSelected ? '#20ac9c' : 'transparent',
                                color: isSelected ? '#fff' : (isCurrentMonth ? 'text.primary' : 'text.disabled'),
                                border: isToday(day) && !isSelected ? '1px solid #20ac9c' : 'none',
                                position: 'relative',
                                '&:hover': {
                                    bgcolor: isSelected ? '#20ac9c' : '#f0f0f0'
                                }
                            }}
                        >
                            {formattedDate}
                            {dayEvents.length > 0 && !isSelected && (
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 2,
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: '#20ac9c'
                                }} />
                            )}
                        </Box>
                    </Grid>
                );
                day = addDays(day, 1);
            }
            rows.push(
                <Grid container spacing={0} key={day}>
                    {days}
                </Grid>
            );
            days = [];
        }
        return <Box>{rows}</Box>;
    };

    const [openDialog, setOpenDialog] = useState(false);
    const [newEvent, setNewEvent] = useState({ title: '', time: '' });

    const handleOpenDialog = () => {
        setNewEvent({ title: '', time: format(new Date(), 'HH:mm') });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleDeleteEvent = async (id_agenda) => {
        try {
            const res = await api.delete(`/agenda/${id_agenda}`);
            if (res.data.status === 'sucesso') {
                setEvents(prev => prev.filter(e => e.id_agenda !== id_agenda));
                if (onEventsChange) onEventsChange();
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Não foi possível excluir o evento da agenda:', error);
            }
        }
    };

    const handleCreateEvent = async () => {
        if (!newEvent.title) return;

        try {
            const payload = {
                st_titulo: newEvent.title,
                st_descricao: '',
                dt_evento: format(selectedDate, 'yyyy-MM-dd'),
                st_hora: newEvent.time
            };

            const res = await api.post('/agenda', payload);

            if (res.data.status === 'sucesso') {
                const parts = res.data.dados.dt_evento.split('-');
                const eventDate = new Date(parts[0], parts[1] - 1, parts[2]);

                const createdEvent = {
                    ...res.data.dados,
                    date: eventDate,
                    title: res.data.dados.st_titulo,
                    time: res.data.dados.st_hora,
                    id_agenda: res.data.dados.id_agenda // Ensure ID is saved
                };
                setEvents(prev => [...prev, createdEvent]);
                handleCloseDialog();
                if (onEventsChange) onEventsChange();
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Não foi possível criar o evento da agenda:', error);
            }
        }
    };

    const renderSelectedDateEvents = () => {
        const dayEvents = events.filter(e => isSameDay(e.date, selectedDate));

        return (
            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#20ac9c' }}>
                        Agenda de {format(selectedDate, "d 'de' MMMM", { locale: ptBR })}
                    </Typography>
                    <IconButton size="small" onClick={handleOpenDialog} sx={{ color: '#20ac9c' }}>
                        <Add fontSize="small" />
                    </IconButton>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : (
                    <Paper variant="outlined" sx={{ maxHeight: 150, overflow: 'auto' }}>
                        {dayEvents.length > 0 ? (
                            <List dense>
                                {dayEvents.map((event, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem
                                            secondaryAction={
                                                <IconButton edge="end" aria-label="delete" size="small" onClick={() => handleDeleteEvent(event.id_agenda)}>
                                                    <Delete fontSize="small" sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }} />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={event.title}
                                                secondary={event.time}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            />
                                        </ListItem>
                                        {index < dayEvents.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Nenhum evento agendado.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}

                <Dialog open={openDialog} onClose={handleCloseDialog}>
                    <DialogTitle>Novo Evento</DialogTitle>
                    <DialogContent sx={{ minWidth: 250 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="title"
                            label="Título do Evento"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={newEvent.title}
                            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            id="time"
                            label="Hora"
                            type="time"
                            fullWidth
                            variant="standard"
                            value={newEvent.time}
                            onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancelar</Button>
                        <Button onClick={handleCreateEvent} variant="contained" sx={{ bgcolor: '#20ac9c', color: '#fff', '&:hover': { bgcolor: '#1a8a7d' } }}>Salvar</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    };

    return (
        <Box sx={{ width: 300, p: 2 }}>
            {renderHeader()}
            {renderDays()}
            {renderCells()}
            <Divider sx={{ my: 2 }} />
            {renderSelectedDateEvents()}
        </Box>
    );
};

export default AgendaComponent;
